// frontend/src/pages/staff/StaffReviews.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import axios from "axios";
import "./staffStyles.css";

function StaffReviews() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    avgRating: 0,
    totalReviews: 0,
    ratingBreakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, 5, 4, 3, 2, 1
  const [sortBy, setSortBy] = useState("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = () =>
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  const fetchReviews = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      setLoading(true);
      const rating = filter === "all" ? null : filter;
      const response = await axios.get(
        `${getApiBaseUrl()}/api/reviews/my-professional-reviews`,
        {
          headers,
          params: {
            page: currentPage,
            limit: 10,
            rating,
            sortBy,
          },
        },
      );

      setReviews(response.data.reviews);
      setStats(response.data.stats);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [getAuthHeaders, filter, sortBy, currentPage]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleRespondClick = (reviewId, currentResponse) => {
    setRespondingTo(reviewId);
    setResponseText(currentResponse || "");
  };

  const handleCancelResponse = () => {
    setRespondingTo(null);
    setResponseText("");
  };

  const handleSubmitResponse = async (reviewId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!responseText.trim()) {
      alert("Please enter a response");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${getApiBaseUrl()}/api/reviews/${reviewId}/response`,
        { responseText: responseText.trim() },
        { headers },
      );

      // Refresh reviews
      await fetchReviews();
      setRespondingTo(null);
      setResponseText("");
      setSubmitting(false);
    } catch (error) {
      alert(error.response?.data?.message || "Failed to submit response");
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              color: star <= rating ? "#fbbf24" : "#d1d5db",
              fontSize: "16px",
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading && reviews.length === 0) {
    return (
      <StaffSidebarLayout>
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>Loading reviews...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout>
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1
          style={{ marginBottom: "24px", fontSize: "24px", fontWeight: "600" }}
        >
          Reviews & Ratings
        </h1>

        {/* Stats Overview */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#1a659e" }}
            >
              {stats.avgRating.toFixed(1)}
            </div>
            <div
              style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px" }}
            >
              Average Rating
            </div>
            {renderStars(Math.round(stats.avgRating))}
          </div>

          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{ fontSize: "32px", fontWeight: "700", color: "#1a659e" }}
            >
              {stats.totalReviews}
            </div>
            <div
              style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px" }}
            >
              Total Reviews
            </div>
          </div>

          {/* Rating Breakdown */}
          <div
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              gridColumn: "span 2",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Rating Breakdown
            </div>
            {[5, 4, 3, 2, 1].map((star) => (
              <div
                key={star}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <span style={{ fontSize: "14px", width: "20px" }}>{star}★</span>
                <div
                  style={{
                    flex: 1,
                    height: "8px",
                    background: "#e5e7eb",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${stats.totalReviews > 0 ? (stats.ratingBreakdown[star] / stats.totalReviews) * 100 : 0}%`,
                      height: "100%",
                      background: "#fbbf24",
                    }}
                  />
                </div>
                <span
                  style={{ fontSize: "14px", color: "#6b7280", width: "30px" }}
                >
                  {stats.ratingBreakdown[star]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Filters and Sort */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          >
            <option value="recent">Most Recent</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div
            style={{
              background: "#fff",
              padding: "40px",
              borderRadius: "8px",
              textAlign: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <p style={{ fontSize: "16px", color: "#6b7280" }}>
              No reviews yet. Keep providing great service!
            </p>
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {reviews.map((review) => (
              <div
                key={review._id}
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {/* Review Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: "12px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontWeight: "600", fontSize: "16px" }}>
                        {review.customerId?.firstName}{" "}
                        {review.customerId?.lastName}
                      </span>
                      {review.isVerified && (
                        <span
                          style={{
                            fontSize: "11px",
                            background: "#dbeafe",
                            color: "#1e40af",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontWeight: "500",
                          }}
                        >
                          ✓ Verified
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginTop: "4px",
                      }}
                    >
                      {formatDate(review.createdAt)}
                      {review.bookingId && (
                        <span>
                          {" "}
                          • Service on{" "}
                          {formatDate(review.bookingId.appointmentDate)}
                        </span>
                      )}
                    </div>
                  </div>
                  {renderStars(review.rating)}
                </div>

                {/* Review Text */}
                <p
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#374151",
                    marginBottom: "16px",
                  }}
                >
                  {review.reviewText}
                </p>

                {/* Business Response */}
                {review.businessResponse && respondingTo !== review._id ? (
                  <div
                    style={{
                      background: "#f9fafb",
                      padding: "16px",
                      borderRadius: "6px",
                      borderLeft: "3px solid #1a659e",
                      marginTop: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1a659e",
                        marginBottom: "8px",
                      }}
                    >
                      Your Response
                    </div>
                    <p
                      style={{
                        fontSize: "14px",
                        lineHeight: "1.6",
                        color: "#374151",
                      }}
                    >
                      {review.businessResponse.text}
                    </p>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "8px",
                      }}
                    >
                      {formatDate(review.businessResponse.respondedAt)}
                    </div>
                    <button
                      onClick={() =>
                        handleRespondClick(
                          review._id,
                          review.businessResponse.text,
                        )
                      }
                      style={{
                        marginTop: "8px",
                        padding: "6px 12px",
                        fontSize: "13px",
                        color: "#1a659e",
                        background: "transparent",
                        border: "1px solid #1a659e",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Edit Response
                    </button>
                  </div>
                ) : respondingTo === review._id ? (
                  <div
                    style={{
                      background: "#f9fafb",
                      padding: "16px",
                      borderRadius: "6px",
                      marginTop: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1a659e",
                        marginBottom: "8px",
                      }}
                    >
                      {review.businessResponse ? "Edit" : "Write"} Your Response
                    </div>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Thank you for your feedback..."
                      maxLength={500}
                      style={{
                        width: "100%",
                        minHeight: "80px",
                        padding: "12px",
                        fontSize: "14px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        resize: "vertical",
                        fontFamily: "inherit",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "8px",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                        {responseText.length}/500 characters
                      </span>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={handleCancelResponse}
                          disabled={submitting}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            color: "#6b7280",
                            background: "#fff",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSubmitResponse(review._id)}
                          disabled={submitting || !responseText.trim()}
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            color: "#fff",
                            background:
                              submitting || !responseText.trim()
                                ? "#9ca3af"
                                : "#1a659e",
                            border: "none",
                            borderRadius: "6px",
                            cursor:
                              submitting || !responseText.trim()
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          {submitting ? "Submitting..." : "Submit Response"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleRespondClick(review._id, null)}
                    style={{
                      marginTop: "8px",
                      padding: "8px 16px",
                      fontSize: "14px",
                      color: "#fff",
                      background: "#1a659e",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    Respond to Review
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              marginTop: "24px",
            }}
          >
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                background: "#fff",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                background: "#fff",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
}

export default StaffReviews;
